'use client'

import { useEffect, useRef } from 'react'
import { MC, SERIF } from '@/lib/mycio/theme'
import { DOC_MAP, type DocType } from './content'

/** Terms / Privacy / Disclosures legal modal. Click-outside or ✕ / Close to dismiss; Esc closes. */
export default function DocModal({ type, onClose }: { type: DocType; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    dialogRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current()
      if (e.key !== 'Tab') return
      const targets = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), [tabindex="0"]') ?? [])
      const first = targets[0]
      const last = targets[targets.length - 1]
      if (!first) { e.preventDefault(); return }
      if (e.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
        e.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
      previousFocus?.focus()
    }
  }, [])

  const doc = DOC_MAP[type]

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={doc.title}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(26,42,38,0.55)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 640,
          maxHeight: '86vh',
          background: MC.surface,
          borderRadius: 16,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 40px 90px -30px rgba(26,42,38,0.6)',
          color: MC.ink,
        }}
      >
        <div style={{ flex: 'none', padding: '24px 30px 18px', borderBottom: '1px solid #E4DCCB', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <p style={{ fontSize: 11.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: MC.faint, fontWeight: 700, margin: '0 0 5px' }}>{doc.kicker}</p>
            <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 28, letterSpacing: '-0.01em', margin: 0, color: MC.ink }}>{doc.title}</h2>
            <p style={{ fontSize: 12.5, color: MC.faint2, margin: '6px 0 0' }}>Effective Jun 2026 · Parsenne, Inc., doing business as MyCIO.ai</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ flex: 'none', width: 36, height: 36, borderRadius: 999, background: '#EEE8DC', border: 'none', color: MC.muted, fontSize: 18, lineHeight: 1, cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
        <div style={{ flex: '1 1 auto', overflowY: 'auto', padding: '24px 30px 28px' }}>
          {doc.body.map((clause) => (
            <div key={clause.h} style={{ marginBottom: 22 }}>
              <h3 style={{ fontSize: 15.5, fontWeight: 700, color: MC.ink, margin: '0 0 7px' }}>{clause.h}</h3>
              <p style={{ fontSize: 14.5, lineHeight: 1.6, color: MC.inkSoft, margin: 0, textWrap: 'pretty' }}>{clause.p}</p>
            </div>
          ))}
        </div>
        <div style={{ flex: 'none', padding: '16px 30px 22px', borderTop: '1px solid #E4DCCB', background: MC.bg, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: MC.faint }}>
            Questions? <a href="mailto:legal@mycio.ai" style={{ color: MC.forest, fontWeight: 600 }}>legal@mycio.ai</a>
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{ fontSize: 14.5, fontWeight: 600, color: MC.bg, background: MC.forest, border: 'none', padding: '11px 22px', borderRadius: 10, cursor: 'pointer' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
